<script setup lang="ts">
import { ref, h } from 'vue'
import { RouterView, useRouter, useRoute } from 'vue-router'
import SideMenu from './SideMenu.vue'
import { useSideMenu } from '@/composables/useSideMenu'

const router = useRouter()
const route = useRoute()
const { getMenuLabelByKey } = useSideMenu()

const collapsed = ref(false)

// 标签页管理
const activeTabKey = ref('dashboard')
const tabs = ref([
  {
    key: 'dashboard',
    label: '仪表盘',
    closable: false
  }
])

// 菜单点击处理
const handleMenuClick = (key: string, item: any) => {
  activeTabKey.value = key
  
  // 检查标签页是否已存在
  const existingTab = tabs.value.find(tab => tab.key === key)
  if (!existingTab) {
    // 添加新标签页，使用菜单数据获取标题
    const label = getMenuLabelByKey(key)
    
    tabs.value.push({
      key: key,
      label: label,
      closable: true
    })
  }
  
  // 路由跳转
  router.push(`/${key}`)
}

// 标签页切换处理
const handleTabChange = (key: string) => {
  activeTabKey.value = key
  router.push(`/${key}`)
}

// 关闭标签页处理
const handleTabEdit = (targetKey: string, action: 'add' | 'remove') => {
  if (action === 'remove') {
    const targetIndex = tabs.value.findIndex(tab => tab.key === targetKey)
    if (targetIndex > -1) {
      tabs.value.splice(targetIndex, 1)
      
      // 如果关闭的是当前激活的标签页，切换到前一个标签页
      if (activeTabKey.value === targetKey) {
        const newActiveKey = tabs.value[targetIndex]?.key || tabs.value[targetIndex - 1]?.key || 'dashboard'
        activeTabKey.value = newActiveKey
        router.push(`/${newActiveKey}`)
      }
    }
  }
}

// 侧边栏折叠状态变化处理
const handleCollapseChange = (newCollapsed: boolean) => {
  collapsed.value = newCollapsed
}
</script>

<template>
  <a-layout style="height: 100vh; overflow: hidden;">
    <!-- 侧边栏 -->
    <a-layout-sider v-model:collapsed="collapsed" :trigger="null" collapsible style="height: 100vh;">
      <SideMenu 
        :collapsed="collapsed"
        @update:collapsed="handleCollapseChange"
        @menu-click="handleMenuClick"
      />
    </a-layout-sider>

    <!-- 主布局 -->
    <a-layout style="height: 100vh; overflow: hidden;">
      <!-- 标签页区域 -->
      <div style="background: #fff; padding: 4px 16px 0 16px; border-bottom: 1px solid #f0f0f0; flex-shrink: 0;">
        <a-tabs
          v-model:activeKey="activeTabKey"
          type="editable-card"
          hide-add
          @edit="handleTabEdit"
          @change="handleTabChange"
          style="margin: 0;"
        >
          <a-tab-pane
            v-for="tab in tabs"
            :key="tab.key"
            :tab="tab.label"
            :closable="tab.closable"
          >
            <!-- 标签页内容区域 -->
            <div class="content-area">
              <RouterView />
            </div>
          </a-tab-pane>
        </a-tabs>
      </div>
    </a-layout>
  </a-layout>
</template>

<style scoped>
.content-area {
  height: calc(100vh - 44px);
  overflow-y: auto;
  padding: 24px;
}

:deep(.ant-layout-sider) {
  position: relative;
}

:deep(.ant-tabs-nav) {
  margin: 0;
}

:deep(.ant-tabs-content-holder) {
  padding: 0;
}

:deep(.ant-tabs-tab) {
  margin: 0 2px 0 0;
}

:deep(.ant-tabs-tab-active) {
  background: #fff;
}

:deep(.ant-layout-header) {
  height: 64px;
  line-height: 64px;
  padding: 0 24px;
}
</style> 